// Revenue & margin view model (SCR-011, FLOW-006, API-032/033). Pure: the filters and their
// validation, the query sent to `GET /v1/admin/revenue/{summary,by-org,by-model,by-month,forecast}`,
// the four stat cards (billed, AWS cost, gross profit, markup and margin — on the chosen cost basis),
// the stacked "Billed by month" chart and the breakdown tables (billed, AWS cost, profit, share),
// the customers not billed and the forecast. Gross profit is the one figure the SPA computes
// rather than reads (REQ-051/ADR-037, formerly "fleet net margin"): the window's profit less the
// AWS cost of `not_billed` customer-months AllCode pays for. Money goes through `ui/money.ts`;
// chart heights are float dollars for geometry only.
import type { components } from "../../api/schema";
import type { ChartBar, ChartSeries } from "@nexus/ui";
import { month, validate, type FieldErrors } from "@nexus/ui";
import { formatUsd, markupBpFrom, microToDollars, ratioBasisPoints, sumMicro, toMicro } from "@nexus/ui";
import { addMonths, currentMonth, longMonthLabel, monthLabel, monthSpan, monthsBetween, shortMonthLabel } from "@nexus/ui";

export type RevenueBasis = components["schemas"]["ExpenseBasisKind"];
export type RevenueTotals = components["schemas"]["RevenueTotals"];
export type RevenueSummary = components["schemas"]["RevenueSummary"];
export type RevenueBreakdown = components["schemas"]["RevenueBreakdown"];
export type RevenueBreakdownItem = components["schemas"]["RevenueBreakdownItem"];
export type NotBilledOrgItem = components["schemas"]["NotBilledOrgItem"];
export type RevenueForecast = components["schemas"]["RevenueForecast"];
export type RevenueForecastItem = components["schemas"]["RevenueForecastItem"];

export const REVENUE_PATH = "/revenue";
export const MAX_WINDOW_MONTHS = 36;

/** The cost basis in the filter bar's segmented control. */
export const BASIS_LABELS: Readonly<Record<RevenueBasis, string>> = {
  list: "AWS list price",
  cur: "Actual AWS bill",
};

export const BASIS_OPTIONS: readonly { value: RevenueBasis; label: string }[] = [
  { value: "list", label: BASIS_LABELS.list },
  { value: "cur", label: BASIS_LABELS.cur },
];

export const NOT_BILLED_EMPTY_MESSAGE = "Every customer with usage in these months is billed.";
export const REVENUE_EMPTY_MESSAGE = "No billed usage in these months.";

export interface RevenueFilters {
  from: string;
  to: string;
  basis: RevenueBasis;
  orgId: string | null;
  model: string | null;
}

export type RevenueTab = "by-org" | "by-model" | "by-month";

export const TABS: readonly { value: RevenueTab; label: string }[] = [
  { value: "by-org", label: "Customers" },
  { value: "by-model", label: "Models" },
  { value: "by-month", label: "Months" },
];

/** What the first column of each breakdown names, and the table's accessible name. */
export const TAB_COLUMN: Readonly<Record<RevenueTab, { header: string; caption: string }>> = {
  "by-org": { header: "Customer", caption: "Revenue by customer" },
  "by-model": { header: "Model", caption: "Revenue by model" },
  "by-month": { header: "Month", caption: "Revenue by month" },
};

export const BREAKDOWN_PATHS = {
  "by-org": "/v1/admin/revenue/by-org",
  "by-model": "/v1/admin/revenue/by-model",
  "by-month": "/v1/admin/revenue/by-month",
} as const satisfies Record<RevenueTab, string>;

/** `YYYY-MM` shifted by `delta` months (may be negative), UTC, no `Date` involved. */
export function shiftMonth(period: string, delta: number): string {
  return addMonths(period, delta);
}

/** The default window: the 6 months up to and including the last complete UTC month. */
export function defaultRevenueFilters(now: Date): RevenueFilters {
  const to = addMonths(currentMonth(now), -1);
  return { from: addMonths(to, -5), to, basis: "list", orgId: null, model: null };
}

/** Client-side mirror of API-032's 422s, so a bad range never round-trips (`window_too_large`, `invalid_range`). */
export function validateRevenueFilters(filters: RevenueFilters): FieldErrors {
  const errors = validate({ from: filters.from, to: filters.to }, { from: [month], to: [month] });
  if (errors.from || errors.to) return errors;
  if (filters.from > filters.to) return { to: "Must be on or after the first month." };
  if (monthSpan(filters.from, filters.to) > MAX_WINDOW_MONTHS) {
    return { to: `Choose at most ${MAX_WINDOW_MONTHS} months: the report covers ${MAX_WINDOW_MONTHS} months at a time.` };
  }
  return {};
}

/** The one message the month range shows under it, or null. */
export function rangeError(errors: FieldErrors): string | null {
  return errors.from ?? errors.to ?? null;
}

export interface RevenueQuery {
  from: string;
  to: string;
  basis: RevenueBasis;
  orgId?: string;
  model?: string;
}

/** Query object for the revenue report and breakdown endpoints (API-032). */
export function revenueQuery(filters: RevenueFilters): RevenueQuery {
  const query: RevenueQuery = { from: filters.from, to: filters.to, basis: filters.basis };
  if (filters.orgId) query.orgId = filters.orgId;
  if (filters.model) query.model = filters.model;
  return query;
}

export interface ForecastQuery {
  period?: string;
  orgId?: string;
}

/** Query object for `GET /v1/admin/revenue/forecast` (API-033); `period` omitted defaults to the current month. */
export function forecastQuery(filters: Pick<RevenueFilters, "orgId">): ForecastQuery {
  const query: ForecastQuery = {};
  if (filters.orgId) query.orgId = filters.orgId;
  return query;
}

// ---- figures on the chosen cost basis ----

export interface BasisFigures {
  billed: bigint;
  awsCost: bigint;
  profit: bigint;
  /** A CUR figure in it is not final yet (basis `cur`). */
  provisional: boolean;
}

/**
 * Billed, AWS cost and profit of a totals row on the chosen basis. On `cur` the cost is the actual
 * AWS bill (`expenseMicroUsd`) and the profit `actualMarginMicroUsd`; where the API has no expense
 * figure, the list cost stands in. `provisional` is the API's own flag (a CUR figure not final).
 */
export function basisFigures(totals: RevenueTotals, basis: RevenueBasis): BasisFigures {
  const billed = toMicro(totals.priceMicroUsd);
  if (basis === "cur" && totals.expenseMicroUsd !== null) {
    const awsCost = toMicro(totals.expenseMicroUsd);
    const profit = totals.actualMarginMicroUsd === null ? billed - awsCost : toMicro(totals.actualMarginMicroUsd);
    return { billed, awsCost, profit, provisional: totals.provisional };
  }
  return {
    billed,
    awsCost: toMicro(totals.listCostMicroUsd),
    profit: toMicro(totals.marginMicroUsd),
    provisional: totals.provisional,
  };
}

/** The AWS cost of `not_billed` customer-months AllCode pays for, on the chosen basis. */
export function notBilledAllcodeCost(notBilled: readonly NotBilledOrgItem[], basis: RevenueBasis): bigint {
  return sumMicro(
    notBilled
      .filter((item) => item.awsPaidBy === "allcode")
      .map((item) => (basis === "cur" && item.expenseMicroUsd !== null ? item.expenseMicroUsd : item.listCostMicroUsd)),
  );
}

/**
 * `summary.marginMicroUsd` minus the AWS list cost of the window's `allcode`-paid `not_billed`
 * customer-months (REQ-051, ADR-037): gross profit on the list basis. Not an API field.
 */
export function fleetNetMarginMicroUsd(summary: Pick<RevenueSummary, "marginMicroUsd" | "notBilled">): bigint {
  return toMicro(summary.marginMicroUsd) - notBilledAllcodeCost(summary.notBilled, "list");
}

/** Gross profit on the chosen basis: billed customers' profit less AllCode's cost of the not-billed ones. */
export function grossProfitMicroUsd(summary: RevenueSummary, basis: RevenueBasis): bigint {
  return basisFigures(summary, basis).profit - notBilledAllcodeCost(summary.notBilled, basis);
}

export interface RevenueStats {
  billed: bigint;
  billedNote: string;
  awsCost: bigint;
  awsCostNote: string;
  grossProfit: bigint;
  grossProfitNote: string;
  /** Profit ÷ AWS cost, in basis points (null without a cost). */
  markupBp: bigint | null;
  /** Profit ÷ billed, in basis points (null without a bill). */
  marginBp: bigint | null;
  provisional: boolean;
}

function plural(count: number, noun: string, many = `${noun}s`): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? noun : many}`;
}

/** The four stat cards: billed, AWS cost, gross profit, and markup beside margin. */
export function revenueStats(summary: RevenueSummary, basis: RevenueBasis, customers: number | null): RevenueStats {
  const figures = basisFigures(summary, basis);
  const notBilledCost = notBilledAllcodeCost(summary.notBilled, basis);
  const months = Math.max(0, monthSpan(summary.from, summary.to));
  const billedParts = [customers === null ? null : plural(customers, "customer"), plural(months, "month")].filter(Boolean);
  return {
    billed: figures.billed,
    billedNote: billedParts.join(" · "),
    awsCost: figures.awsCost,
    awsCostNote: basis === "cur" ? (figures.provisional ? "Actual AWS bill, not final yet" : "Actual AWS bill") : "At AWS list price",
    grossProfit: figures.profit - notBilledCost,
    grossProfitNote:
      notBilledCost === 0n
        ? "Billed minus AWS cost"
        : `Billed minus AWS cost, less ${formatUsd(notBilledCost)} for customers not billed`,
    markupBp: markupBpFrom(figures.profit, figures.awsCost),
    marginBp: ratioBasisPoints(figures.profit, figures.billed),
    provisional: figures.provisional,
  };
}

/** A one-line note when part of the window is still open (preview), or null when every month is closed. */
export function provisionalNote(summary: Pick<RevenueSummary, "provisional" | "openMonths">): string | null {
  if (!summary.provisional && summary.openMonths.length === 0) return null;
  if (summary.openMonths.length === 0) return "Some AWS costs in these months come from a Cost and Usage Report that AWS has not marked final yet.";
  const months = summary.openMonths.map(monthLabel).join(", ");
  const verb = summary.openMonths.length === 1 ? "is" : "are";
  return `Preview: ${months} ${verb} still open, so ${summary.openMonths.length === 1 ? "its" : "their"} figures come from the live ledger and can still change.`;
}

export interface BreakdownData {
  byOrg: RevenueBreakdown | null;
  byModel: RevenueBreakdown | null;
  byMonth: RevenueBreakdown | null;
}

/** Which of the three fetched breakdowns the active tab shows. */
export function selectBreakdown(tab: RevenueTab, data: BreakdownData): RevenueBreakdown | null {
  switch (tab) {
    case "by-org":
      return data.byOrg;
    case "by-model":
      return data.byModel;
    case "by-month":
      return data.byMonth;
  }
}

export interface BreakdownRow {
  key: string;
  label: string;
  billed: bigint;
  awsCost: bigint;
  profit: bigint;
  /** This row's share of the rows' total billed, 0..1. */
  share: number;
  /** A CUR figure is not final (basis `cur`). */
  provisional: boolean;
  /** An open month read from the live ledger (Months tab only). */
  open: boolean;
}

function shareOf(part: bigint, total: bigint): number {
  if (total <= 0n || part <= 0n) return 0;
  return Number((part * 10_000n) / total) / 10_000;
}

/** The breakdown table's rows on the chosen basis, biggest bill first (months stay in order). */
export function breakdownRows(tab: RevenueTab, items: readonly RevenueBreakdownItem[], basis: RevenueBasis, openMonths: readonly string[] = []): BreakdownRow[] {
  const figures = items.map((item) => ({ item, figures: basisFigures(item, basis) }));
  const total = figures.reduce((sum, row) => sum + (row.figures.billed > 0n ? row.figures.billed : 0n), 0n);
  const rows = figures.map(({ item, figures: f }) => ({
    key: item.key,
    label: tab === "by-month" ? monthLabel(item.key) : item.key === "plan_level" ? "Plan fees, minimums and credits" : item.label || item.key,
    billed: f.billed,
    awsCost: f.awsCost,
    profit: f.profit,
    share: shareOf(f.billed, total),
    provisional: f.provisional,
    open: tab === "by-month" && openMonths.includes(item.key),
  }));
  if (tab === "by-month") return rows.sort((a, b) => a.key.localeCompare(b.key));
  return rows.sort((a, b) => (a.billed === b.billed ? a.label.localeCompare(b.label) : a.billed > b.billed ? -1 : 1));
}

/** "42%" / "<1%" for the share column. */
export function shareLabel(share: number): string {
  if (share <= 0) return "0%";
  if (share < 0.01) return "<1%";
  return `${Math.round(share * 100)}%`;
}

// ---- the stacked "Billed by month" chart ----

export const REVENUE_SERIES: readonly ChartSeries[] = [
  { id: "cost", label: "AWS cost", class: "series-cost" },
  { id: "profit", label: "Profit", class: "series-profit" },
];

/**
 * One stacked bar per month of the window (a month without billed usage is an empty bar): AWS cost
 * at the bottom, profit on top, open months drawn lighter. The tooltip carries the exact figures.
 */
export function monthChartBars(
  items: readonly RevenueBreakdownItem[],
  window: { from: string; to: string },
  basis: RevenueBasis,
  openMonths: readonly string[] = [],
): ChartBar[] {
  const byKey = new Map(items.map((item) => [item.key, item]));
  return monthsBetween(window.from, window.to).map((period) => {
    const item = byKey.get(period);
    const f = item ? basisFigures(item, basis) : { billed: 0n, awsCost: 0n, profit: 0n, provisional: false };
    const open = openMonths.includes(period);
    const note = open ? " (open month, preview)" : "";
    return {
      key: period,
      label: shortMonthLabel(period),
      tooltip: `${monthLabel(period)}${note}: billed ${formatUsd(f.billed)}, AWS cost ${formatUsd(f.awsCost)}, profit ${formatUsd(f.profit)}`,
      segments: [
        { series: "cost", value: microToDollars(f.awsCost) },
        { series: "profit", value: microToDollars(f.profit) },
      ],
      muted: open,
    };
  });
}

// ---- forecast ----

export interface ForecastRowView {
  orgId: string;
  monthToDatePriceMicroUsd: number;
  runRateMicroUsdPerDay: number;
  projectedPriceMicroUsd: number;
  daysElapsed: number;
  daysInMonth: number;
  progressPct: number;
}

/** The forecast table's rows: month to date, run-rate and projected, per customer. */
export function forecastRows(forecast: RevenueForecast): ForecastRowView[] {
  return forecast.items.map((item) => ({
    orgId: item.orgId,
    monthToDatePriceMicroUsd: item.monthToDate.priceMicroUsd,
    runRateMicroUsdPerDay: item.runRateMicroUsdPerDay,
    projectedPriceMicroUsd: item.projected.priceMicroUsd,
    daysElapsed: item.daysElapsed,
    daysInMonth: item.daysInMonth,
    progressPct: item.daysInMonth === 0 ? 0 : Math.round((item.daysElapsed / item.daysInMonth) * 100),
  }));
}

/** "Forecast for September 2026". */
export function forecastTitle(period: string): string {
  return `Forecast for ${longMonthLabel(period)}`;
}

/** The not-billed table's months: "Jul 2026, Aug 2026". */
export function monthsLabel(months: readonly string[]): string {
  return months.map(monthLabel).join(", ");
}

export const AWS_PAID_BY_LABELS: Readonly<Record<NotBilledOrgItem["awsPaidBy"], string>> = {
  allcode: "AllCode",
  client: "Customer",
};
